import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { connectRedux } from '../../helpers/connectRedux'
import { Button, Spinner, ValidatedInput } from '../../components'
import { isPasswordValid } from '../../helpers'
import { LiteralsService } from '../../services'
import { setNewPasswordRequiredPasswordAction, setNewPasswordRequiredSavePasswordAction } from '../../store/actions'
import { IStoreState } from '../../store/states'

interface IProps extends DispatchProp<AnyAction> {
  language: string
  password: string
  isSigningIn: boolean
}

interface IState {
  repeatPassword: string
}

/**
 * The new password Cognito asks for after signing in with a temporary one, shared by the page and
 * the guest prompt. Saving it completes the sign in, and the session check takes over from there.
 */
class NewPasswordRequiredForm extends PureComponent<IProps, IState> {
  public state: IState = { repeatPassword: '' }

  private get buttonIsDisabled(): boolean {
    const { password, isSigningIn } = this.props
    return !isPasswordValid(password) || password !== this.state.repeatPassword || isSigningIn
  }

  private get passwordAlert(): ReactNode {
    const { password } = this.props

    if (!password || isPasswordValid(password)) return null
    return (
      <div className="error-container">
        <span className="error">
          { LiteralsService.get('validPassword', true) }
        </span>
      </div>
    )
  }

  private get saveButton(): ReactNode {
    if (this.props.isSigningIn) return <div className="loading"><Spinner /></div>
    return (
      <Button
        title={ LiteralsService.get('continue') }
        onPress={ this.handleSave }
        filled
        upper
        isDisabled={ this.buttonIsDisabled }
      />
    )
  }

  public render(): ReactNode {
    const { password } = this.props
    const { repeatPassword } = this.state

    return (
      <>
        <section>
          <ValidatedInput
            name="password"
            label={ LiteralsService.get('password', true) }
            value={ password }
            onChange={ this.onPasswordChange }
            error={ !isPasswordValid(password) }
            isPassword
          />
          { this.passwordAlert }
          <ValidatedInput
            name="repeat-password"
            label={ LiteralsService.get('repeatPassword', true) }
            value={ repeatPassword }
            onChange={ this.onRepeatPasswordChange }
            error={ !isPasswordValid(repeatPassword) || password !== repeatPassword }
            isPassword
          />
        </section>
        <section className="actions">
          { this.saveButton }
        </section>
      </>
    )
  }

  private onPasswordChange = (password: string): void => {
    this.props.dispatch(setNewPasswordRequiredPasswordAction(password))
  }

  private onRepeatPasswordChange = (repeatPassword: string): void => {
    this.setState({ repeatPassword })
  }

  private handleSave = (): void => {
    this.props.dispatch(setNewPasswordRequiredSavePasswordAction())
  }
}

const mapStateToProps = ({ newPasswordRequired, signIn, configuration }: IStoreState) => ({
  language: configuration.language,
  password: newPasswordRequired.password,
  isSigningIn: signIn.isSigningIn
})

export default connectRedux(mapStateToProps, NewPasswordRequiredForm) as React.ComponentType<{}>
